import { message as aMessage } from "ant-design-vue";
import { bannerStatuses } from "@/src/constants/bannerConstants.js";
import { redirectToLeadsOrContacts } from "@/src/utils/callsUtils.js";
import { telephonyProviders } from "@/src/constants/telephonyConstants.js";
import {
  callStatuses,
  conferenceParticipantLabels,
} from "@/src/constants/callConstants.js";
import store from "@/src/store/index.js";
import eventBus from "@/src/utils/eventBus.js";
import BaseTelephonyEventHandler from "@/src/services/telephony/base/baseTelephonyEventHandler.js";

class TwilioEventHandler extends BaseTelephonyEventHandler {
  constructor() {
    super(telephonyProviders.twilio);
    this.messageHandlers = new Set();
  }

  initialize(device) {
    this.device = device;
    this.setupMessageListeners();
    this.setupDeviceListeners();
  }

  setupMessageListeners() {
    if (!this.device) return;

    this.device.on("messageReceived", (message) => {
      this.messageHandlers.forEach((handler) => {
        this.handleCustomMessage(message).then();
      });
    });
  }

  setupDeviceListeners() {
    if (!this.device) return;

    this.device.on("error", (error) => {
      console.log("Device message error: ", error);
    });
  }

  registerConnectionMessageHandlers(connection) {
    if (!connection) return;

    connection.on("messageReceived", (message) => {
      this.handleCustomMessage(message).then();
    });
  }

  async sendMessage(connection, message) {
    if (!connection) {
      console.error("No active connection to send message");
      return null;
    }

    try {
      const callMessage = {
        content: message,
        messageType: "user-defined-message",
        contentType: "application/json",
      };

      const voiceEventSid = await connection.sendMessage(callMessage);
      console.log("Message sent successfully: ", voiceEventSid);
      return voiceEventSid;
    } catch (error) {
      console.error("Error sending message:", error);
      throw error;
    }
  }

  async handleCustomMessage(message) {
    try {
      const content = JSON.parse(JSON.stringify(message.content));

      if (content?.name === "caller_on_hold") {
        const participants = store.getters["conferenceCall/participants"];
        await store.dispatch("conferenceCall/setTransferringCall", false);

        await store.dispatch(
          "conferenceCall/setCallerOnHoldEventReceived",
          true,
        );

        await store.dispatch(
          "conferenceCall/mergeParticipants",
          content.participants,
        );

        const storedConference =
          store.getters["conferenceCall/conferenceData"];
        const isClientIdentity = (s) =>
          typeof s === "string" && s.startsWith("client:");
        const safeName =
          content?.transfer_name?.trim() ||
          storedConference?.transferName ||
          "";
        const safeNumber = isClientIdentity(content?.transfer_number)
          ? storedConference?.transferPhoneNumber || null
          : content?.transfer_number;

        await store.dispatch("conferenceCall/addParticipant", {
          conference_sid: content.conference_id,
          label: conferenceParticipantLabels.TRANSFER_TARGET,
          status: callStatuses.PENDING,
          hold: false,
          muted: false,
          phone_number: safeNumber,
          name: safeName,
          end_conference_on_exit: true,
          start_conference_on_enter: true,
        });

        const updatedParticipants =
          store.getters["conferenceCall/participants"];
      }

      if (content?.transfer_target_join) {
        await store.dispatch("conferenceCall/setTransferTargetActive", true);
      }

      if (content?.transfer_target_left) {
        const wasActive = store.getters["conferenceCall/isWarmTransfer"];
        eventBus.emit("cancel-transfer-abort");
        await store.dispatch(
          "conferenceCall/cancelWarmTransfer",
          conferenceParticipantLabels.TRANSFER_TARGET,
        );
        if (wasActive) {
          aMessage.info("Transfer ended");
        }
      }

      if (content?.transfer_complete) {
        await store.dispatch("conferenceCall/addParticipant", {
          ...content?.transfer_complete,
          name: content?.transfer_complete?.name?.trim(),
          hold: false,
          label: conferenceParticipantLabels.TRANSFER_TARGET,
        });
      }

      if (content?.name === "conference_merged") {
        // Twilio fans the user-defined message out to every participant call leg,
        // so the agent's own client receives it too. Dedupe on transfer_id to avoid
        // re-applying the merge state on every echo.
        const lastId =
          store.getters["conferenceCall/lastMergedTransferId"];
        if (lastId && lastId === content.transfer_id) {
          return;
        }
        await store.dispatch(
          "conferenceCall/markMerged",
          content.transfer_id,
        );
        if (Array.isArray(content?.participants)) {
          await store.dispatch(
            "conferenceCall/setParticipants",
            content.participants.map((p) => ({
              ...p,
              hold: false,
            })),
          );
        }
      }

      if (content?.name === "participant_left") {
        // Only act on participant_left in merged mode — legacy non-merged warm
        // transfer flows have their own dedicated events (transfer_target_left
        // etc) and must not be affected by this handler.
        if (!store.getters["conferenceCall/isMerged"]) {
          return;
        }
        if (content?.call_sid) {
          await store.dispatch(
            "conferenceCall/removeParticipant",
            content.call_sid,
          );
        }
      }

      if (content?.campaign_pickup) {
        await store.dispatch("callsCampaign/updateCallOngoing", true);
        await store.dispatch(
          "dynamicBanner/updateBannerStatus",
          bannerStatuses.CONNECTED,
        );
        eventBus.emit("call-campaign-pickup");
        await redirectToLeadsOrContacts(content?.campaign_pickup);
      }

      if (content?.campaign_call_update) {
        await store.dispatch("callsCampaign/refreshCampaignDetails");
      }
    } catch (error) {
      console.error("An error occurred while handling message: ", error);
    }
  }

  handleStatusUpdate(content) {
    console.log(content);
  }
}

const twilioEventHandler = new TwilioEventHandler();
export default twilioEventHandler;
